'use client';

import { type InternalLink, InternalLinkPaths } from '@bb/lib/types';
import type { AdBanner, ContentTextEditor, Product } from '@bb/sanity.types';
import {
  PortableText,
  type PortableTextComponents,
  type PortableTextTypeComponentProps,
} from '@portabletext/react';
import Link from 'next/link';
import type { Image } from 'sanity';
import { AdvertisementBanner } from '../Advertisment/BlogAdvertisement/BlogAdvertisement';
import { Anchor } from '../Anchor';
import { trimTitleToSlug } from '../Blog/utils/trimTitleToSlug';
import { ProductCard } from './PageComponents';
import { SanityImageComponent } from './SanityImageComponent';

const customComponentTypes = {
  image: (props: PortableTextTypeComponentProps<Image>) => (
    <SanityImageComponent {...props} style={{ maxHeight: '25rem' }} />
  ),
  product: (props: PortableTextTypeComponentProps<Product>) => {
    return <ProductCard product={props.value} />;
  },
  adBanner: (props: PortableTextTypeComponentProps<AdBanner>) => {
    return <AdvertisementBanner adBanner={props.value} />;
  },
} as const;

export type CustomComponentTypes = keyof typeof customComponentTypes;

const components: PortableTextComponents = {
  types: customComponentTypes,
  block: {
    h1: (props) => <h1 className="title">{props.children}</h1>,
    h2: (props) =>
      props.children ? (
        <Anchor tag={trimTitleToSlug(props.children?.toString())}>
          <h2 className="title">{props.children}</h2>
        </Anchor>
      ) : (
        props.children
      ),
    h3: (props) =>
      props.children ? (
        <Anchor tag={trimTitleToSlug(props.children?.toString())}>
          <h3 className="title">{props.children}</h3>
        </Anchor>
      ) : (
        props.children
      ),
    h4: (props) => <h4 className="font-semibold!">{props.children}</h4>,
    h5: (props) => <h5 className="font-[500]!">{props.children}</h5>,
  },
  marks: {
    em: (props) => <em className="title">{props.children}</em>,
    link: (props) => {
      const linkHref = props.value.href;

      if (!linkHref) {
        return props.children;
      }

      if (!linkHref.startsWith('/')) {
        // external link
        return (
          <a
            className="title hover:underline"
            rel="noopener noreferrer"
            target="_blank"
            href={linkHref}
          >
            {props.children}
          </a>
        );
      }

      if (InternalLinkPaths[linkHref as InternalLink]) {
        return (
          <Link
            className="title hover:underline"
            href={InternalLinkPaths[linkHref as InternalLink]}
          >
            {props.children}
          </Link>
        );
      }

      return (
        <Link
          className="title w-full hover:underline"
          prefetch={false}
          href={linkHref}
        >
          {props.children}
        </Link>
      );
    },
  },
};

export function ContentRenderer({
  content,
}: {
  content: ContentTextEditor;
}) {
  return (
    <PortableText
      onMissingComponent={(type) => {
        console.log('missing component', type);
      }}
      value={content}
      components={components}
    />
  );
}
